// Store image helpers: thumbnail (resize) only for item lists (sourcing items).
// Categories and families show cover (full image); item list uses thumbnail for fast load.
// Requires Supabase Pro for Image Transform; otherwise we fall back to cover URL.

import { safeTrim } from '@/lib/utils';
import { SITE_URL } from '@/lib/site-config';

const SUPABASE_STORAGE_OBJECT = '/storage/v1/object/public/';
const SUPABASE_STORAGE_RENDER = '/storage/v1/render/image/public/';

const DEFAULT_THUMB = { width: 200, height: 200 };

/**
 * Returns a thumbnail URL from the cover/main image URL.
 * If the URL is from our Supabase bucket, returns the Image Transform URL (resized).
 * Otherwise returns the same URL (use img size in CSS for external URLs).
 * Pro plan: Supabase serves resized image. Free: transform URL may 404 — fallback to coverUrl.
 * We force `resize=contain` with white background to avoid cropping.
 */
export function getThumbnailUrl(
  coverUrl: string | null | undefined,
  size: { width?: number; height?: number } = DEFAULT_THUMB
): string | null {
  const url = safeTrim(coverUrl);
  if (!url) return null;
  if (!url.includes(SUPABASE_STORAGE_OBJECT)) return url;
  const renderPath = url.replace(SUPABASE_STORAGE_OBJECT, SUPABASE_STORAGE_RENDER);
  const w = size.width ?? DEFAULT_THUMB.width;
  const h = size.height ?? DEFAULT_THUMB.height;
  const sep = renderPath.includes('?') ? '&' : '?';
  // Avoid cropping: contain + white background so images keep original aspect.
  return `${renderPath}${sep}width=${w}&height=${h}&resize=contain&background=ffffff`;
}

/** Whether the URL is from Supabase Storage (so we can use transform for thumbnail). */
export function isSupabaseStorageUrl(url: string | null | undefined): boolean {
  return !!safeTrim(url).includes(SUPABASE_STORAGE_OBJECT);
}

/**
 * Returns an absolute URL for og:image and meta. If already https, returns as-is.
 * If relative path, prepends VITE_SUPABASE_URL; otherwise uses SITE_URL for default assets.
 */
export function toAbsoluteImageUrl(url: string | null | undefined): string | null {
  const u = safeTrim(url);
  if (!u) return null;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = import.meta.env.VITE_SUPABASE_URL ?? SITE_URL;
  const path = u.startsWith('/') ? u : `/${u}`;
  return `${base.replace(/\/$/, '')}${path}`;
}
